import { Clock } from 'lucide-react'
import { Button } from '@teach/ui'
import { jobTypeLabel } from '@/features/ai/lib/jobTypeLabel'
import type { StudioJobStatus, StudioJobType } from '@/features/ai/studioTypes'
import { useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

interface Props {
  jobId: number
  jobType: StudioJobType
  /**
   * Статус занявшего прогона. `awaiting_author` — курс занят ВАМИ же: прогон
   * стоит в ленте с вопросом, и звать надо ответить, а не ждать. Типизировано
   * `StudioJobStatus` (Р-2, финальное ревью), а не голым `string`: источник —
   * `ActiveJob.status` (`studioStartTypes.ts`), у него та же типизация ровно
   * затем, чтобы опечатка в сравнении ниже ловилась сборкой, а не молчала до
   * прода. Пустой строки старого ответа бэка сюда не доходит — она законна
   * только у `StartFailure['status']` (`parseStartError.ts`, отказ 409).
   */
  status: StudioJobStatus
  onShow: () => void
}

/**
 * Проактивный гейт «курс занят»: рисуется ДО клика на старт, из
 * `quote.active_job` — тот же источник, что кормит 409 на бэке, поэтому
 * расхождения «панель говорит можно, а старт отказал» не будет.
 *
 * `onShow` перецепляет панель на чужой прогон. Метода `attach` у `useCourseJob`
 * нет: перецепление это прямой `dispatch(attachJob(...))` в `useCourseBusyGate`,
 * который и собирает запись реестра (jobId/type известны здесь, параметров
 * чужого запуска мы не знаем — они остаются `null`).
 */
export const CourseBusyNotice = ({ jobId, jobType, status, onShow }: Props) => {
  const m = useMessages(messages)
  const isAwaitingAuthor = status === 'awaiting_author'
  return (
    <div
      data-testid="course-busy-notice"
      role="status"
      className="m-3 space-y-2.5 rounded-2xl border border-warning/30 bg-warning/10 p-4 text-sm"
    >
      <div className="flex items-center gap-2 text-warning">
        <Clock className="h-5 w-5 shrink-0" />
        <span className="font-medium">
          {isAwaitingAuthor ? m.busyAwaiting(jobId) : m.busyCourse(jobId, jobTypeLabel(jobType))}
        </span>
      </div>
      <Button variant="outline" size="sm" className="w-full" onClick={onShow}>
        {isAwaitingAuthor ? m.answerQuestion : m.showRun}
      </Button>
    </div>
  )
}
